/**
 * Dice roller utility for chat commands.
 *
 * Syntax:
 *   /roll NdX[+/-M] [avantage=K|desavantage=K]
 *   /roll aide
 *
 * Pure JS — no Flarum or Mithril dependencies.
 */

const DICE_REGEX = /^\/roll\s+(\d*)d(\d+)\s*([+-]\d+)?\s*(?:(avantage|desavantage)\s*=\s*(\d+))?\s*$/i;
const HELP_REGEX = /^\/roll\s+aide\s*$/i;

/**
 * Returns true if text is a valid /roll command (dice or aide).
 */
export function isDiceCommand(text) {
  if (typeof text !== 'string') return false;
  const trimmed = text.trim();
  return HELP_REGEX.test(trimmed) || DICE_REGEX.test(trimmed);
}

/**
 * Returns true if text is the /roll aide help command.
 */
export function isHelpCommand(text) {
  if (typeof text !== 'string') return false;
  return HELP_REGEX.test(text.trim());
}

/**
 * Returns a multiline help string in French.
 */
export function getHelpText() {
  return [
    '**Commandes de d\u00e9s**',
    '',
    '`/roll NdX` \u2014 Lance N d\u00e9s \u00e0 X faces',
    '`/roll NdX+M` \u2014 Avec un modificateur (+M ou -M)',
    '`/roll NdX avantage=K` \u2014 Lance N d\u00e9s, garde les K meilleurs',
    '`/roll NdX desavantage=K` \u2014 Lance N d\u00e9s, garde les K plus bas',
    '',
    '**Limites :** N entre 1 et 100, X entre 2 et 1000, modificateur entre -999 et +999.',
    'Pour avantage/d\u00e9savantage, K doit \u00eatre \u2265 1 et < N.',
    '',
    '**Exemples :**',
    '`/roll d20` \u2014 Lance 1d20',
    '`/roll 2d6+3` \u2014 Lance 2d6 avec +3',
    '`/roll 4d6 avantage=3` \u2014 Lance 4d6, garde les 3 meilleurs',
    '`/roll 2d20 desavantage=1` \u2014 Lance 2d20, garde le plus bas',
    '`/roll aide` \u2014 Affiche cette aide',
  ].join('\n');
}

/**
 * Parses and executes a /roll command.
 *
 * @param {string} text - The full command text
 * @returns {{ text: string } | { error: string }}
 */
export function rollDice(text) {
  if (typeof text !== 'string') return { error: 'Commande invalide.' };

  const trimmed = text.trim();

  if (isHelpCommand(trimmed)) {
    return { text: getHelpText() };
  }

  const match = trimmed.match(DICE_REGEX);
  if (!match) {
    return { error: 'Commande invalide. Tapez `/roll aide` pour l\u2019aide.' };
  }

  const n = match[1] === '' ? 1 : parseInt(match[1], 10);
  const x = parseInt(match[2], 10);
  const modifier = match[3] ? parseInt(match[3], 10) : 0;
  const keepMode = match[4] ? match[4].toLowerCase() : null; // 'avantage' or 'desavantage'
  const keepCount = match[5] ? parseInt(match[5], 10) : 0;

  // Validation
  if (n < 1 || n > 100) {
    return { error: 'Le nombre de d\u00e9s (N) doit \u00eatre entre 1 et 100.' };
  }
  if (x < 2 || x > 1000) {
    return { error: 'Le nombre de faces (X) doit \u00eatre entre 2 et 1000.' };
  }
  if (Math.abs(modifier) > 999) {
    return { error: 'Le modificateur doit \u00eatre entre -999 et +999.' };
  }
  if (keepMode) {
    if (keepCount < 1 || keepCount >= n) {
      return { error: 'K doit \u00eatre \u2265 1 et strictement inf\u00e9rieur \u00e0 N.' };
    }
  }

  // Roll the dice
  const rolls = [];
  for (let i = 0; i < n; i++) {
    rolls.push(Math.floor(Math.random() * x) + 1);
  }

  // Determine which dice to keep
  let keptIndices;
  if (keepMode) {
    // Create array of { value, index } and sort to find kept dice
    const indexed = rolls.map((v, i) => ({ value: v, index: i }));
    if (keepMode === 'avantage') {
      // Keep K highest
      indexed.sort((a, b) => b.value - a.value);
    } else {
      // Keep K lowest
      indexed.sort((a, b) => a.value - b.value);
    }
    const keptSet = new Set(indexed.slice(0, keepCount).map((d) => d.index));
    keptIndices = keptSet;
  }

  // Build the dice display string (original roll order, discarded use strikethrough)
  const diceDisplay = rolls
    .map((v, i) => {
      if (keepMode && !keptIndices.has(i)) {
        return '~~' + v + '~~';
      }
      return String(v);
    })
    .join(', ');

  // Calculate total
  let total;
  if (keepMode) {
    total = rolls.reduce((sum, v, i) => (keptIndices.has(i) ? sum + v : sum), 0);
  } else {
    total = rolls.reduce((sum, v) => sum + v, 0);
  }
  total += modifier;

  // Build the label: NdX part
  const label = (n === 1 && match[1] === '' ? '' : n) + 'd' + x + (match[3] || '');

  // Build the keep mode label
  let keepLabel = '';
  if (keepMode === 'avantage') {
    keepLabel = ' (avantage ' + keepCount + ')';
  } else if (keepMode === 'desavantage') {
    keepLabel = ' (d\u00e9savantage ' + keepCount + ')';
  }

  // Build modifier display
  let modifierDisplay = '';
  if (modifier > 0) {
    modifierDisplay = ' +' + modifier;
  } else if (modifier < 0) {
    modifierDisplay = ' ' + modifier;
  }

  // Assemble output
  // NdX[+/-M] [(avantage K)] → [dice] [+/-M] = **total**
  const output =
    '\uD83C\uDFB2 ' +
    label +
    keepLabel +
    ' \u2192 [' +
    diceDisplay +
    ']' +
    modifierDisplay +
    ' = **' +
    total +
    '**';

  return { text: output };
}
